<template>
  <PublicLayout
    variant="no-bg"
    :breadcrumbs="breadcrumbs"
    :hero="false"
    breadcrumbs-in-content
    :ui="{
      main: 'pt-0!',
    }"
  >
    <UContainer>
      <PageContentSlider
        class="mb-10 max-xl:mb-7 max-sm:mb-5"
        title="Подобрать школу"
        description="Выберите интересующие вас параметры, чтобы найти подходящую школу"
        :counter="{
          amount: '500 тыс',
          text: 'Школ на портале',
        }"
        image="/images/schools/cup.webp"
        :slides="schoolsWithLinks"
        to-prefix="/schools"
        location-badge
        :loading="pending"
        :ui="{
          contentCard: 'max-lg:max-w-full max-xl:max-w-87.5',
          image: 'max-lg:w-100 max-lg:left-3/7 max-lg:top-5/7',
          counter: 'h-26.5!',
          title: 'max-lg:inline max-lg:mr-h1-paragraph max-md:block',
          description: 'line-clamp-none',
        }"
      >
        <template #slide-badges="{ slide }">
          <UBadge
            v-for="(badge, index) in slide?.lesson_types"
            :key="index"
            class="rounded-[250px] text-white ring-white bg-transparent"
            variant="subtle"
          >
            {{ badge.name }}
          </UBadge>
        </template>

        <template #slide-button="{ slide }">
          <UButton
            class="mb-10.5 max-lg:w-max max-lg:mb-h1-paragraph text-button-secondary-foreground max-sm:hidden"
            trailing-icon="i-local-move-up-right"
            color="white"
            :to="'/schools/' + slide.id"
            :ui="{
              trailingIcon: 'text-button-secondary-foreground',
            }"
          >
            Подробнее
          </UButton>
        </template>
      </PageContentSlider>

      <PageSearchFilter
        v-model:directions="selectedDirections"
        v-model:lesson-types="selectedLessonTypes"
        v-model:query="query"
        class="mb-10 max-xl:mb-7 max-sm:mb-5"
        placeholder="Найти школу"
        @search="search"
      />

      <ExpandableList v-slot="{ displayItems }" :items="schools" :limit="9">
        <CommonGridCards :items="displayItems" :pending="pending">
          <template #card="{ item }">
            <CommonCard
              :item="item"
              :to="`/schools/${item.id}`"
              image-top-left="lesson-types"
              image-top-right="rating"
              title-above-left="directions"
              :title-above-right="getLabelIcons(item)"
              :ui="{
                title: 'max-md:line-clamp-2 max-md:max-w-full!',
              }"
            />
          </template>
        </CommonGridCards>
      </ExpandableList>
    </UContainer>

    <JoinUsSection />
  </PublicLayout>
</template>

<script setup lang="ts">
import type { LessonTypeRef, School } from '~/api'
import JoinUsSection from '~/components/main-page/JoinUsSection.vue'
import ExpandableList from '~/components/ExpandableList.vue'
import PublicLayout from '~/layouts/PublicLayout.vue'
import type {
  BreadcrumbItem,
  CardLink,
  DirectionMenuItem,
  IconWithLabel,
  SearchParams,
} from '~/types'

defineOptions({
  name: 'SchoolsPage',
})

const breadcrumbs: BreadcrumbItem[] = [
  {
    name: 'Главная',
    to: '/',
  },
  {
    name: 'Школы',
  },
]

definePageMeta({
  breadcrumbs,
})

const route = useRoute()
const directoryStore = useDirectoryStore()
const { schools } = storeToRefs(directoryStore)

const { untilReady } = useRegionReady()

const { pending } = await useAppAsyncData(`schools-${route.path}`, async () => {
  await untilReady()
  await Promise.all([
    directoryStore.fetchDirections(),
    directoryStore.fetchLessonTypes(),
    directoryStore.fetchSchools(),
  ])

  return true
})

const selectedDirections = ref<DirectionMenuItem[]>([])
const selectedLessonTypes = ref<LessonTypeRef[]>([])
const query = ref('')

const schoolsWithLinks = computed(() =>
  schools.value.map((school) => ({
    ...school,
    links: [
      {
        icon: 'i-local-coach',
        text: school.trainers_count,
        color: 'bg-accent-default',
      },
      {
        icon: 'i-local-location',
        text: school.region?.name ?? '',
        color: 'bg-add-light',
      },
    ] as CardLink[],
  })),
)

const getLabelIcons = (item: School): IconWithLabel[] => [
  {
    icon: 'i-local-coach',
    label: 'Тренеры',
    value: item.trainers_count,
    to: `/schools/${item.id}/trainers`,
  },
]

const search = async (query?: SearchParams) => {
  await directoryStore.fetchSchools(query)
}
</script>

<style lang="scss" scoped>
:deep(.common-card) {
  --card-height: 445px;
  --card-image-height: 240px;

  @include max-xl {
    --card-height: 358px;
    --card-image-height: 200px;
  }

  @include max-md {
    --card-height: 226px;
    --card-image-height: 130px;
  }
}

.join-us-section {
  margin-top: var(--page-section-padding);
}
</style>
